// src/components/payrolls/ApprovalSection.jsx
import React, { useState, useMemo } from "react";
import { preparePayroll, checkPayroll, approvePayroll, rejectPayroll } from "../../services/payroll.service";
import { ChevronDown, ChevronUp, Users, DollarSign, CheckCircle, Clock, UserCheck, ShieldCheck, XCircle, XOctagon } from "lucide-react";

const ApprovalSection = ({ payrolls, refresh }) => {
  const [loadingPeriod, setLoadingPeriod] = useState(null);
  const [comments, setComments] = useState("");
  const [error, setError] = useState("");
  const [expandedPeriods, setExpandedPeriods] = useState(new Set());
  
  // Individual Rejection State
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");

  // Bulk Rejection State
  const [showBulkReject, setShowBulkReject] = useState(false);
  const [bulkRejectReason, setBulkRejectReason] = useState("");

  const formatCurrency = (amount) => {
    return `$${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDateLog = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getActorName = (actor) => {
    if (!actor) return "System";
    const first = actor.firstName || actor.firstname || "";
    const last = actor.lastName || actor.lastname || "";
    if (!first && !last) return "Unknown User";
    return `${first} ${last}`;
  };

  // --- 1. Group Payrolls ---
  const payrollGroups = useMemo(() => {
    const groups = {};
    payrolls.forEach((p) => {
      if (!p.payrollPeriod?.year || !p.payrollPeriod?.month) return;
      const y = p.payrollPeriod.year;
      const m = String(p.payrollPeriod.month).padStart(2, '0');
      const periodKey = `${y}-${m}`;

      if (!groups[periodKey]) {
        groups[periodKey] = {
          year: y,
          month: p.payrollPeriod.month,
          periodKey,
          records: [],
          totalNet: 0,
          counts: {
            prepared: 0,
            checked: 0,
            approved: 0,
            rejected: 0,
            total: 0
          }
        };
      }

      groups[periodKey].records.push(p);
      groups[periodKey].totalNet += (p.netSalary || 0);
      groups[periodKey].counts.total += 1;

      const ap = p.approvalProcess || {};
      if (ap.preparedBy) groups[periodKey].counts.prepared += 1;
      if (ap.checkedBy) groups[periodKey].counts.checked += 1;
      if (ap.approvedBy) groups[periodKey].counts.approved += 1;
      if (ap.rejectedBy) groups[periodKey].counts.rejected += 1; 
    });

    const sortedKeys = Object.keys(groups).sort().reverse();
    return sortedKeys.map(key => groups[key]);
  }, [payrolls]);

  const handleBulkMonthAction = async (periodKey, actionType) => {
    setError("");
    
    // Handle Reject Button Click -> Show Input
    if (actionType === 'reject') {
      setShowBulkReject(true);
      return;
    }

    setLoadingPeriod(periodKey);
    const group = payrollGroups.find(g => g.periodKey === periodKey);
    if (!group) return;

    try {
      const targets = group.records.filter(p => {
        const ap = p.approvalProcess || {};
        if (actionType === 'prepare') return !ap.preparedBy; 
        if (actionType === 'check') return ap.preparedBy && !ap.checkedBy;
        if (actionType === 'approve') return ap.checkedBy && !ap.approvedBy;
        return false;
      });

      if (targets.length === 0) {
        alert("No records require this action at this time.");
        setLoadingPeriod(null);
        return;
      }

      const promises = targets.map(p => {
        if (actionType === 'approve') return approvePayroll(p._id, { comments });
        if (actionType === 'check') return checkPayroll(p._id);
        if (actionType === 'prepare') return preparePayroll(p._id);
      });

      await Promise.all(promises);
      setComments("");
      refresh();
    } catch (err) {
      console.error("Bulk action error:", err);
      setError(err.message || "Failed to complete bulk action.");
    } finally {
      setLoadingPeriod(null);
    }
  };

  const handleConfirmBulkReject = async (periodKey, nextStep) => {
    if (!bulkRejectReason.trim()) {
      alert("Please provide a reason for rejection.");
      return;
    }
    
    setLoadingPeriod(periodKey);
    const group = payrollGroups.find(g => g.periodKey === periodKey);
    if (!group) return;

    try {
      // Determine targets based on current stage
      const targets = group.records.filter(p => {
        const ap = p.approvalProcess || {};
        // If next step is Approve, we are currently Checked. Reject Checked items.
        if (nextStep === 'approve') return ap.checkedBy && !ap.approvedBy;
        // If next step is Complete, we are currently Approved. Reject Approved items.
        if (nextStep === 'complete') return ap.approvedBy;
        return false;
      });

      if (targets.length === 0) {
        alert("No records eligible for rejection at this stage.");
        setLoadingPeriod(null);
        return;
      }

      const promises = targets.map(p => rejectPayroll(p._id, { reason: bulkRejectReason }));
      await Promise.all(promises);
      
      setBulkRejectReason("");
      setShowBulkReject(false);
      refresh();
    } catch (err) {
      console.error("Bulk reject error:", err);
      setError(err.message || "Failed to complete bulk rejection.");
    } finally {
      setLoadingPeriod(null);
    }
  };

  const handleReject = async (id) => {
    if (!rejectionReason.trim()) {
      alert("Please provide a reason for rejection.");
      return;
    }
    try {
      await rejectPayroll(id, { reason: rejectionReason });
      setRejectingId(null);
      setRejectionReason("");
      refresh();
    } catch (err) {
      console.error("Rejection error:", err);
      alert("Failed to reject payroll: " + (err.message || "Unknown error"));
    }
  };

  const getNextStep = (counts) => {
    if (counts.approved === counts.total) return 'complete';
    if (counts.checked === counts.total) return 'approve';
    if (counts.prepared === counts.total) return 'check';
    return 'prepare';
  };

  const toggleExpand = (key) => {
    const newSet = new Set(expandedPeriods);
    if (newSet.has(key)) newSet.delete(key);
    else newSet.add(key);
    setExpandedPeriods(newSet);
  };

  const getMonthName = (m) => new Date(0, m - 1).toLocaleString('default', { month: 'long' });

  return (
    <div className="mt-6 bg-white shadow rounded p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-800">Monthly Payroll Approval</h2>
        <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
          {payrollGroups.length} Period(s) Available
        </span>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-600 rounded border border-red-200 text-sm">
          {error}
        </div>
      )}

      <div className="space-y-6">
        {payrollGroups.length === 0 ? (
          <div className="text-center py-10 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
            <p className="text-gray-500">No payroll periods found to review.</p>
          </div>
        ) : (
          payrollGroups.map((group) => {
            const { periodKey, month, year, records, totalNet, counts } = group;
            const nextStep = getNextStep(counts);
            const isExpanded = expandedPeriods.has(periodKey);
            
            const prepPct = (counts.prepared / counts.total) * 100;
            const checkPct = (counts.checked / counts.total) * 100;
            const apprPct = (counts.approved / counts.total) * 100;

            return (
              <div key={periodKey} className="border rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow">
                
                {/* Card Header */}
                <div className="bg-gradient-to-r from-gray-50 to-white p-5 border-b border-gray-100">
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div>
                      <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                        {getMonthName(month)} {year}
                        {nextStep === 'complete' && <CheckCircle className="w-5 h-5 text-green-600" />}
                        {counts.rejected > 0 && nextStep !== 'complete' && <XCircle className="w-5 h-5 text-orange-500" title="Contains rejected items"/>}
                      </h3>
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                        <span className="flex items-center gap-1"><Users className="w-4 h-4"/> {counts.total} Staff</span>
                        <span className="flex items-center gap-1"><DollarSign className="w-4 h-4"/> Total Net: {formatCurrency(totalNet)}</span>
                        {counts.rejected > 0 && (
                           <span className="flex items-center gap-1 text-red-600 font-bold"><XOctagon className="w-4 h-4"/> {counts.rejected} Rejected</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                       <button 
                          onClick={() => toggleExpand(periodKey)}
                          className="text-sm flex items-center gap-1 text-orange-600 hover:text-orange-700 font-medium px-3 py-1.5 rounded-lg hover:bg-orange-50 transition-colors"
                        >
                          {isExpanded ? <><ChevronUp className="w-4 h-4"/> Hide Details</> : <><ChevronDown className="w-4 h-4"/> View Staff Details</>}
                        </button>
                    </div>
                  </div>
                </div>

                {/* Visual Workflow Progress */}
                <div className="p-6 bg-white">
                  <div className="grid grid-cols-3 gap-4 mb-6 relative">
                    <div className="absolute top-6 left-[16%] right-[16%] h-1 bg-gray-200 -z-0 hidden md:block"></div>

                    {/* Step 1: Prepare */}
                    <div className="relative z-10 flex flex-col items-center text-center">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center border-4 border-white shadow-sm transition-all
                        ${counts.prepared === counts.total ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-600'}`}>
                        {counts.prepared}/{counts.total}
                      </div>
                      <span className="text-xs font-bold text-gray-700 mt-2 uppercase tracking-wide">Prepare</span>
                    </div>

                    {/* Step 2: Check */}
                    <div className="relative z-10 flex flex-col items-center text-center">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center border-4 border-white shadow-sm transition-all
                        ${counts.checked === counts.total ? 'bg-yellow-500 text-white' : 'bg-yellow-100 text-yellow-600'}`}>
                        {counts.checked}/{counts.total}
                      </div>
                      <span className="text-xs font-bold text-gray-700 mt-2 uppercase tracking-wide">Check</span>
                    </div>

                    {/* Step 3: Approve */}
                    <div className="relative z-10 flex flex-col items-center text-center">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center border-4 border-white shadow-sm transition-all
                        ${counts.approved === counts.total ? 'bg-green-600 text-white' : 'bg-green-100 text-green-600'}`}>
                        {counts.approved}/{counts.total}
                      </div>
                      <span className="text-xs font-bold text-gray-700 mt-2 uppercase tracking-wide">Approve</span>
                    </div>
                  </div>

                  {/* Action Bar */}
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <div className="flex flex-col sm:flex-row items-end sm:items-center justify-between gap-3">
                      <div className="text-sm text-gray-600">
                        {nextStep === 'prepare' && "Payroll needs to be prepared for review."}
                        {nextStep === 'check' && "Payroll is prepared. Ready for verification."}
                        {nextStep === 'approve' && "Payroll is checked. Ready for final approval."}
                        {nextStep === 'complete' && "This payroll period is fully approved and ready for payment."}
                      </div>
                      
                      <div className="flex items-center gap-2 w-full sm:w-auto flex-wrap">
                        
                        {/* BULK REJECT INPUT UI */}
                        {showBulkReject ? (
                          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto animate-fade-in">
                            <textarea 
                              className="w-full sm:w-64 border border-red-300 rounded p-2 text-xs focus:outline-none focus:ring-2 focus:ring-red-500" 
                              rows="2"
                              placeholder="Enter rejection reason..."
                              value={bulkRejectReason}
                              onChange={(e) => setBulkRejectReason(e.target.value)}
                            ></textarea>
                            <div className="flex gap-2">
                                <button 
                                    onClick={() => handleConfirmBulkReject(periodKey, nextStep)}
                                    disabled={loadingPeriod === periodKey}
                                    className="bg-red-600 text-white px-4 py-2 rounded text-sm font-semibold shadow-sm hover:bg-red-700 disabled:opacity-50"
                                >
                                    {loadingPeriod === periodKey ? '...' : 'Confirm Reject'}
                                </button>
                                <button 
                                    onClick={() => { setShowBulkReject(false); setBulkRejectReason(""); }}
                                    className="text-gray-500 hover:text-gray-700 px-2 py-1"
                                >
                                    Cancel
                                </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            {/* COMMENTS INPUT (Only for Approve) */}
                            {nextStep === 'approve' && (
                              <textarea 
                                className="w-full sm:w-64 border rounded p-2 text-xs focus:ring-2 focus:ring-green-500 focus:outline-none" 
                                rows="1"
                                placeholder="Add approval comments..."
                                value={comments}
                                onChange={(e) => setComments(e.target.value)}
                              ></textarea>
                            )}

                            {/* REJECT BUTTON (Visible at Approve or Complete stage) */}
                            {(nextStep === 'approve' || nextStep === 'complete') && (
                                <button
                                  onClick={() => handleBulkMonthAction(periodKey, 'reject')}
                                  className="text-xs border border-red-200 text-red-600 hover:bg-red-50 px-4 py-2 rounded font-semibold shadow-sm transition-colors flex items-center gap-1"
                                >
                                  <XOctagon className="w-3 h-3" /> Reject
                                </button>
                            )}

                            {/* MAIN ACTION BUTTON (Hidden if Complete) */}
                            {nextStep !== 'complete' && (
                              <button
                                onClick={() => handleBulkMonthAction(periodKey, nextStep)}
                                disabled={loadingPeriod === periodKey}
                                className={`${
                                  nextStep === 'prepare' ? 'bg-blue-600 hover:bg-blue-700' :
                                  nextStep === 'check' ? 'bg-yellow-500 hover:bg-yellow-600 text-white' :
                                  'bg-green-600 hover:bg-green-700'
                                } text-white px-6 py-2 rounded text-sm font-semibold shadow-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2`}
                              >
                                {loadingPeriod === periodKey ? (
                                  <span>Processing...</span>
                                ) : (
                                  <>
                                    {nextStep === 'prepare' && 'Prepare All'}
                                    {nextStep === 'check' && 'Check All'}
                                    {nextStep === 'approve' && 'Approve All'}
                                    <span className="bg-white/20 px-1.5 py-0.5 rounded text-xs">
                                      {nextStep === 'prepare' && counts.total - counts.prepared}
                                      {nextStep === 'check' && counts.total - counts.checked}
                                      {nextStep === 'approve' && counts.total - counts.approved}
                                    </span>
                                  </>
                                )}
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Collapsible Staff Details WITH TIMESTAMPS & REJECTION UI */}
                {isExpanded && (
                  <div className="border-t border-gray-100 bg-gray-50 p-4 animate-fade-in-down">
                    <h4 className="text-xs font-bold text-gray-500 uppercase mb-3">Staff Breakdown & Audit Trail</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {records.map((p) => {
                        const ap = p.approvalProcess || {};
                        const isPrep = !!ap.preparedBy;
                        const isChk = !!ap.checkedBy;
                        const isAppr = !!ap.approvedBy;
                        const isRej = !!ap.rejectedBy; 
                        
                        let statusColor = "bg-gray-200 text-gray-600";
                        let statusText = "Pending";

                        if (isRej && !isAppr && !isChk) { statusColor = "bg-red-100 text-red-700"; statusText = "Rejected (Check)"; }
                        else if (isRej && !isAppr && isChk) { statusColor = "bg-red-100 text-red-700"; statusText = "Rejected (Approve)"; }
                        else if (isAppr) { statusColor = "bg-green-100 text-green-700"; statusText = "Approved"; }
                        else if (isChk) { statusColor = "bg-yellow-100 text-yellow-700"; statusText = "Checked"; }
                        else if (isPrep) { statusColor = "bg-blue-100 text-blue-700"; statusText = "Prepared"; }

                        return (
                          <div key={p._id} className="bg-white p-3 rounded border border-gray-200 shadow-sm flex flex-col justify-between">
                            
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <div className="font-medium text-sm text-gray-900">{p.nameSnapshot}</div>
                                <div className="text-xs text-gray-500 font-mono">{formatCurrency(p.netSalary)}</div>
                              </div>
                              <span className={`text-[10px] px-2 py-1 rounded-full font-bold uppercase ${statusColor} whitespace-nowrap`}>
                                {statusText}
                              </span>
                            </div>

                            {isRej && (
                              <div className="mb-2 p-2 bg-red-50 border border-red-100 rounded text-xs text-red-800">
                                <div className="font-bold flex justify-between">
                                    <span>Rejected Reason:</span>
                                    <span className="font-normal text-[9px] text-red-500">{formatDateLog(ap.rejectedAt)}</span>
                                </div>
                                <div>{ap.rejectionReason || "No reason provided"}</div>
                                <div className="text-[9px] text-red-500 mt-1">by {getActorName(ap.rejectedBy)}</div>
                              </div>
                            )}

                            <div className="mt-2 pt-2 border-t border-gray-100 space-y-2">
                              {isPrep && (
                                <div className="flex items-start gap-2">
                                  <div className="mt-0.5 text-blue-500"><Clock className="w-3 h-3" /></div>
                                  <div className="text-[10px] text-gray-600 leading-tight">
                                    <span className="font-semibold text-blue-700 block">Prepared</span>
                                    <span>{getActorName(ap.preparedBy)}</span>
                                    <div className="text-[9px] text-gray-400">{formatDateLog(ap.preparedAt)}</div>
                                  </div>
                                </div>
                              )}
                              {isChk && (
                                <div className="flex items-start gap-2">
                                  <div className="mt-0.5 text-yellow-600"><UserCheck className="w-3 h-3" /></div>
                                  <div className="text-[10px] text-gray-600 leading-tight">
                                    <span className="font-semibold text-yellow-700 block">Checked</span>
                                    <span>{getActorName(ap.checkedBy)}</span>
                                    <div className="text-[9px] text-gray-400">{formatDateLog(ap.checkedAt)}</div>
                                  </div>
                                </div>
                              )}
                              {isAppr && (
                                <div className="flex items-start gap-2">
                                  <div className="mt-0.5 text-green-600"><ShieldCheck className="w-3 h-3" /></div>
                                  <div className="text-[10px] text-gray-600 leading-tight">
                                    <span className="font-semibold text-green-700 block">Approved</span>
                                    <span>{getActorName(ap.approvedBy)}</span>
                                    <div className="text-[9px] text-gray-400">{formatDateLog(ap.approvedAt)}</div>
                                  </div>
                                </div>
                              )}
                              {!isPrep && !isChk && !isAppr && !isRej && (
                                <div className="text-[10px] italic text-gray-400 pl-5">No actions taken yet.</div>
                              )}
                            </div>

                            {/* Individual Action Buttons */}
                            {isChk && !isAppr && !isRej && (
                                <div className="mt-3 pt-2 border-t border-dashed border-gray-200 grid grid-cols-2 gap-2">
                                    <button 
                                        onClick={() => handleReject(p._id)}
                                        className="text-xs border border-red-200 text-red-600 hover:bg-red-50 py-1.5 rounded flex items-center justify-center gap-1 transition-colors"
                                    >
                                        <XOctagon className="w-3 h-3" /> Reject
                                    </button>
                                </div>
                            )}

                            {isAppr && !isRej && (
                                <div className="mt-3 pt-2 border-t border-dashed border-gray-200 grid grid-cols-2 gap-2">
                                    <button 
                                        onClick={() => handleReject(p._id)}
                                        className="text-xs border border-red-200 text-red-600 hover:bg-red-50 py-1.5 rounded flex items-center justify-center gap-1 transition-colors"
                                    >
                                        <XOctagon className="w-3 h-3" /> Reject
                                    </button>
                                </div>
                            )}

                            {rejectingId === p._id && (
                                <div className="mt-2 bg-white p-2 rounded border border-red-200 shadow-sm animate-fade-in">
                                    <textarea 
                                        className="w-full text-xs border border-red-300 rounded p-1 focus:outline-none focus:ring-1 focus:ring-red-500"
                                        placeholder="Enter rejection reason..."
                                        rows="2"
                                        value={rejectionReason}
                                        onChange={(e) => setRejectionReason(e.target.value)}
                                    ></textarea>
                                    <div className="flex gap-2 justify-end mt-2">
                                        <button onClick={() => { setRejectingId(null); setRejectionReason(""); }} className="text-xs text-gray-500 hover:text-gray-700">Cancel</button>
                                        <button 
                                            onClick={() => handleReject(p._id)} 
                                            className="text-xs bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
                                        >
                                            Confirm
                                        </button>
                                    </div>
                                </div>
                            )}

                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}

              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ApprovalSection;